import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { tap } from 'rxjs';
import { AdminCompetenceRoutes } from 'src/app/core/enums/routes.enum';
import { ApiResponse } from 'src/app/core/models/api-response';
import { getDepartmentId } from 'src/app/services/store-snapshot.service';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { IdName } from 'src/app/shared/models/id-name';
import { ICompetenceRoleModel, ICompetenceRoleSimple } from '../model/competence-role.model';
import { CompetenceRolesApiService } from '../services/competence-roles-api.service';
import {
  ClearDetailedRole,
  CreateCompetenceRole,
  DeleteCompetenceRole,
  LoadAllCompetenceRoles,
  LoadAllCompetenceRolesForCurrentDepartment,
  LoadCompetenceRoleById,
  LoadCompetenceRoles,
  UpdateCompetenceRole,
} from './competence-roles.actions';

export interface CompetenceRolesStateModel {
  roles: IdName[];
  allRoles: ICompetenceRoleSimple[];
  allRolesForCurrentDepartment: ICompetenceRoleSimple[];
  currentRole: ICompetenceRoleModel | undefined;
  rolesTotalPages: number;
}

const COMPETENCE_ROLES_STATE_TOKEN = new StateToken<CompetenceRolesStateModel>('competenceRoles');

@State<CompetenceRolesStateModel>({
  name: COMPETENCE_ROLES_STATE_TOKEN,
  defaults: {
    roles: [],
    allRoles: [],
    allRolesForCurrentDepartment: [],
    currentRole: undefined,
    rolesTotalPages: 0,
  },
})
@Injectable()
export class CompetenceRolesState {
  constructor(private service: CompetenceRolesApiService) {}

  @Action(LoadCompetenceRoles, { cancelUncompleted: true })
  loadCompetenceRoles(
    { getState, setState }: StateContext<CompetenceRolesStateModel>,
    { filter }: LoadCompetenceRoles
  ) {
    return this.service.getCompetenceRolesList(filter).pipe(
      tap((response: ApiResponse<IdName[]>) => {
        const state = getState();
        setState({
          ...state,
          roles: response.data,
          rolesTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadAllCompetenceRoles, { cancelUncompleted: true })
  loadAllCompetenceRoles(
    { getState, setState }: StateContext<CompetenceRolesStateModel>,
    { includeDeleted, departmentId }: LoadAllCompetenceRoles
  ) {
    return this.service.getAllCompetenceRoles(includeDeleted, departmentId).pipe(
      tap((allRoles: ICompetenceRoleSimple[]) => {
        const state = getState();
        setState({
          ...state,
          allRoles,
        });
      })
    );
  }

  @Action(LoadAllCompetenceRolesForCurrentDepartment, { cancelUncompleted: true })
  loadAllCompetenceRolesForCurrentDepartment(
    { getState, setState }: StateContext<CompetenceRolesStateModel>,
    { includeDeleted }: LoadAllCompetenceRolesForCurrentDepartment
  ) {
    return this.service.getAllCompetenceRoles(includeDeleted, getDepartmentId()).pipe(
      tap((allRolesForCurrentDepartment: ICompetenceRoleSimple[]) => {
        const state = getState();
        setState({
          ...state,
          allRolesForCurrentDepartment,
        });
      })
    );
  }

  @Action(LoadCompetenceRoleById, { cancelUncompleted: true })
  loadCompetenceRoleById(
    { getState, setState }: StateContext<CompetenceRolesStateModel>,
    { id }: LoadCompetenceRoleById
  ) {
    return this.service.getCompetenceRoleById(id).pipe(
      tap((role: ICompetenceRoleModel) => {
        const state = getState();
        setState({
          ...state,
          currentRole: role,
        });
      })
    );
  }

  @Action(CreateCompetenceRole, { cancelUncompleted: true })
  @OnSuccess({ url: AdminCompetenceRoutes.Roles, message: 'roleCreatedSuccessfully' })
  createCompetenceRole(
    ctx: StateContext<CompetenceRolesStateModel>,
    { role }: CreateCompetenceRole
  ) {
    return this.service.addCompetenceRole(role);
  }

  @Action(UpdateCompetenceRole, { cancelUncompleted: true })
  @OnSuccess({ url: AdminCompetenceRoutes.Roles, message: 'roleUpdatedSuccessfully' })
  updateCompetenceRole(
    ctx: StateContext<CompetenceRolesStateModel>,
    { role }: UpdateCompetenceRole
  ) {
    return this.service.updateCompetenceRole(role);
  }

  @Action(DeleteCompetenceRole, { cancelUncompleted: true })
  @OnSuccess({ url: AdminCompetenceRoutes.Roles, message: 'roleDeletedSuccessfully' })
  deleteCompetenceRole(ctx: StateContext<CompetenceRolesStateModel>, { id }: DeleteCompetenceRole) {
    return this.service.deleteCompetenceRole(id);
  }

  @Action(ClearDetailedRole)
  clearDetailedRole(ctx: StateContext<CompetenceRolesStateModel>) {
    ctx.setState(patch({ currentRole: undefined }));
  }

  @Selector()
  static getRoles(state: CompetenceRolesStateModel): IdName[] {
    return state.roles;
  }

  @Selector()
  static getAllRoles(state: CompetenceRolesStateModel): ICompetenceRoleSimple[] {
    return state.allRoles;
  }

  @Selector()
  static getAllRolesForCurrentDepartment(
    state: CompetenceRolesStateModel
  ): ICompetenceRoleSimple[] {
    return state.allRolesForCurrentDepartment;
  }

  @Selector()
  static getRolesTotalPages(state: CompetenceRolesStateModel): number {
    return state.rolesTotalPages;
  }

  @Selector()
  static getDetailedRole(state: CompetenceRolesStateModel): ICompetenceRoleModel | undefined {
    return state.currentRole;
  }
}
